import {useMemo} from 'react';

/**
 * Клиентский поиск по витрине курсов.
 *
 * Ищем не только по названию, но и по темам модулей: типовой сценарий — HR ищет
 * «управление рисками», курса с таким названием нет, но это тема внутри двух программ.
 * Поэтому карточка умеет показать строку «Найдено в программе: Тема 5 — ...».
 *
 * Данных на 12 курсов мало, индекс собирается один раз в useMemo и живёт в памяти;
 * запрос на сервер не нужен.
 */

const LAYOUT_EN = "qwertyuiop[]asdfghjkl;'zxcvbnm,.";
const LAYOUT_RU = "йцукенгшщзхъфывапролджэячсмитьбю";

/** Исправляет запрос, набранный в латинской раскладке: eghfdktybt -> управление. */
const fixLayout = (s) => s.replace(/[a-z[\];',.]/g, (ch) => {
    const i = LAYOUT_EN.indexOf(ch);
    return i === -1 ? ch : LAYOUT_RU[i];
});

// Грубый стеммер: отсекает частые русские окончания у слов длиннее пяти букв.
// Полноценная морфология здесь избыточна — словарь витрины маленький и предсказуемый.
const TAIL = /(ами|ями|ого|его|ыми|ими|ой|ей|ов|ев|ах|ях|ам|ям|ы|и|а|я|у|ю|е|о)$/;
const stem = (w) => (w.length > 5 ? w.replace(TAIL, '') : w);

/** Строка -> массив нормализованных основ. */
export const tokenize = (raw) => {
    if (!raw) return [];
    return String(raw)
        .toLowerCase()
        .replace(/ё/g, 'е')
        // «ИИ» и «AI» — одно и то же понятие, в текстах встречается и так, и так
        .replace(/\bии\b/g, 'ai')
        .replace(/[^a-zа-я0-9]+/g, ' ')
        .trim()
        .split(' ')
        .filter(Boolean)
        .map(stem);
};

const collect = (pick, course) => {
    const out = [];
    const push = (v) => {
        const s = pick(v);
        if (s) out.push(s);
    };
    push(course.title);
    push(course.subtitle);
    push(course.cardDescription);
    push(course.goal);
    push(course.finalProduct);
    push(course.durationShape);
    (course.targetAudience || []).forEach(push);
    (course.forBusiness || []).forEach(push);
    (course.outcomes || []).forEach((o) => {
        push(o.title);
        push(o.text);
    });
    (course.faq || []).forEach((f) => {
        push(f.q);
        push(f.a);
    });
    if (course.trainer) {
        push(course.trainer.name);
        push(course.trainer.position);
        push(course.trainer.credentials);
    }
    out.push(course.category, course.format, course.level, ...(course.audienceTags || []));
    return out.join(' ');
};

/** Все ли основы запроса нашлись в наборе (по префиксу, чтобы «риск» находил «риски»). */
const matchesAll = (haystack, queryTokens) =>
    queryTokens.every((qt) => {
        for (let i = 0; i < haystack.length; i += 1) {
            if (haystack[i].startsWith(qt)) return true;
        }
        return false;
    });

const countMatches = (haystack, queryTokens) =>
    queryTokens.reduce((acc, qt) => {
        for (let i = 0; i < haystack.length; i += 1) {
            if (haystack[i].startsWith(qt)) return acc + 1;
        }
        return acc;
    }, 0);

export const useCoursesSearch = (courses, query, pick) => {
    const index = useMemo(() => courses.map((course) => ({
        slug: course.slug,
        course,
        cardTokens: tokenize(collect(pick, course)),
        modules: (course.modules || []).map((m) => ({
            n: m.n,
            title: pick(m.title),
            tokens: tokenize(`${pick(m.title)} ${pick(m.content)}`),
        })),
    })), [courses, pick]);

    return useMemo(() => {
        const trimmed = (query || '').trim();
        if (!trimmed) {
            return {
                active: false,
                slugs: courses.map((c) => c.slug),
                hints: {},
                ranked: [],
            };
        }

        // Пробуем и как есть, и с исправленной раскладкой — что даст больше попаданий
        const direct = tokenize(trimmed);
        const swapped = tokenize(fixLayout(trimmed.toLowerCase()));

        const evaluate = (queryTokens) => {
            const slugs = [];
            const hints = {};
            const ranked = [];
            index.forEach((entry) => {
                const inCard = matchesAll(entry.cardTokens, queryTokens);
                let hitModule = null;
                if (!inCard) {
                    hitModule = entry.modules.find((m) => matchesAll(m.tokens, queryTokens)) || null;
                }
                const score = countMatches(entry.cardTokens, queryTokens)
                    + (hitModule ? countMatches(hitModule.tokens, queryTokens) : 0);
                if (inCard || hitModule) {
                    slugs.push(entry.slug);
                    if (hitModule) hints[entry.slug] = hitModule;
                }
                if (score > 0) ranked.push({slug: entry.slug, score});
            });
            ranked.sort((a, b) => b.score - a.score);
            return {active: true, slugs, hints, ranked};
        };

        const primary = evaluate(direct);
        if (primary.slugs.length > 0 || swapped.join(' ') === direct.join(' ')) return primary;

        const alternative = evaluate(swapped);
        return alternative.slugs.length > 0 ? alternative : primary;
    }, [index, query, courses]);
};

export default useCoursesSearch;
